/**
 * InlineAppointmentMessage Component
 *
 * An appointment booking widget rendered inline within the chat interface.
 * Displays an Acuity Scheduling iframe after a user completes payment,
 * allowing them to book a consultation time.
 *
 * UX Priority: Clear instructions and easy access to the scheduling interface.
 */

import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Calendar, CheckCircle, Loader2 } from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

export interface InlineAppointmentMessageProps {
  /** URL for the Acuity scheduling iframe */
  iframeUrl: string;
  /** Title shown above the booking widget */
  title?: string;
  /** Instructions for the user */
  instructions?: string;
  /** Callback when user indicates they've completed booking */
  onBookingComplete?: () => void;
}

// ============================================================================
// Styled Components
// ============================================================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Container = styled.div`
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 1px solid #86efac;
  border-radius: 12px;
  padding: 20px;
  max-width: 100%;
  animation: ${fadeIn} 0.3s ease-out;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const IconWrapper = styled.div`
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 2px 8px rgba(5, 150, 105, 0.3);
`;

const TitleSection = styled.div`
  flex: 1;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: #065f46;
`;

const Subtitle = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: #059669;
  font-weight: 500;
`;

const Instructions = styled.div`
  background: white;
  border: 1px solid #a7f3d0;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 16px;
  font-size: 14px;
  color: #047857;
  line-height: 1.5;
`;

const IframeContainer = styled.div`
  position: relative;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background: white;
  border: 1px solid #d1fae5;
  min-height: 600px;

  @media (min-width: 768px) {
    min-height: 800px;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: white;
  z-index: 1;
  gap: 12px;
`;

const LoadingSpinner = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
  color: #059669;
`;

const LoadingText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #6b7280;
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 600px;
  border: none;

  @media (min-width: 768px) {
    height: 800px;
  }
`;

const SuccessContainer = styled.div`
  text-align: center;
  padding: 32px 20px;
  animation: ${fadeIn} 0.3s ease-out;
`;

const SuccessIcon = styled.div`
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: white;

  svg {
    width: 32px;
    height: 32px;
  }
`;

const SuccessTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
  color: #065f46;
`;

const SuccessMessage = styled.p`
  margin: 0;
  font-size: 14px;
  color: #059669;
`;

const CompletedButton = styled.button`
  margin-top: 16px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  color: #059669;
  background: white;
  border: 1px solid #86efac;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #f0fdf4;
    border-color: #059669;
  }
`;

// ============================================================================
// Main Component
// ============================================================================

export const InlineAppointmentMessage: React.FC<InlineAppointmentMessageProps> = ({
  iframeUrl,
  title = 'Book Your Consultation',
  instructions = 'Please select a time that works best for you to schedule your consultation.',
  onBookingComplete,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleBookingComplete = () => {
    setIsCompleted(true);
    onBookingComplete?.();
  };

  // Show success state if booking is marked as completed
  if (isCompleted) {
    return (
      <Container>
        <SuccessContainer>
          <SuccessIcon>
            <CheckCircle />
          </SuccessIcon>
          <SuccessTitle>Appointment Scheduled</SuccessTitle>
          <SuccessMessage>
            You should receive a confirmation email shortly with the meeting details.
          </SuccessMessage>
        </SuccessContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <IconWrapper>
          <Calendar size={22} />
        </IconWrapper>
        <TitleSection>
          <Title>{title}</Title>
          <Subtitle>Payment confirmed</Subtitle>
        </TitleSection>
      </Header>

      <Instructions>
        {instructions}
      </Instructions>

      <IframeContainer>
        {isLoading && (
          <LoadingOverlay>
            <LoadingSpinner size={32} />
            <LoadingText>Loading scheduler...</LoadingText>
          </LoadingOverlay>
        )}

        <StyledIframe
          src={iframeUrl}
          onLoad={handleIframeLoad}
          title="Book Appointment"
          allow="payment"
        />
      </IframeContainer>

      <CompletedButton onClick={handleBookingComplete}>
        I've completed my booking
      </CompletedButton>
    </Container>
  );
};

export default InlineAppointmentMessage;
